// src/features/works/components/ToolsSection.jsx - ACTUALIZADO CON VISUALIZACIÓN DE PERFIL
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { FaPlus, FaMinus, FaCubes, FaTags } from 'react-icons/fa';

// Componentes - AHORA USANDO LOS COMPONENTES DEL MÓDULO WORKS
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import AssignEquipmentModal from './AssignEquipmentModal'; // ✅ NUEVO: Componente específico de Works
import UnassignEquipmentModal from './UnassignEquipmentModal'; // ✅ NUEVO: Componente específico de Works
import ToolProfileModal from '../../tools/components/ToolProfileModal'; // ✅ NUEVO: Modal reutilizable para perfil

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

// Constantes y utilidades
import { TIPOS_EQUIPOS, getTipoDescripcion } from '../../../utils/constants';

/**
 * Sección para gestionar las herramientas asignadas a una obra - CON COMPONENTES ESPECÍFICOS
 */
const ToolsSection = ({ workId, tools = [], onDataChange, showSnackbar, isWorkFinalized = false }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showToolProfileModal, setShowToolProfileModal] = useState(false); // ✅ NUEVO: Estado para modal de perfil
  const [selectedToolId, setSelectedToolId] = useState(null); // ✅ NUEVO: ID de herramienta seleccionada
  const [filteredTools, setFilteredTools] = useState(tools);

  // Value getter para el tipo de herramienta
  const typeValueGetter = useCallback((tool) => {
    return getTipoDescripcion(tool.tool?.type, TIPOS_EQUIPOS);
  }, []);

  // Value getter para el nombre de herramienta
  const nameValueGetter = useCallback((tool) => {
    return tool.tool?.name || '';
  }, []);

  // ✅ NUEVO: Función para ver perfil de herramienta asignada
  const handleViewTool = useCallback((tool) => {
    //console.log(`🔍 [OBRAS] Abriendo perfil de herramienta asignada ID: ${tool.id}`);
    setSelectedToolId(tool.id);
    setShowToolProfileModal(true);
  }, []);

  // Configuración de filtros
  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(tools, 'tool.name', nameValueGetter, `work-${workId}-tools`);

  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType 
  } = useColumnFilter(tools, 'tool.type', typeValueGetter, `work-${workId}-tools`);

  // Actualizar herramientas filtradas cuando cambian los datos o filtros
  useEffect(() => {
    let result = [...tools];
    
    // Aplicar filtros en cadena
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    setFilteredTools(result);
  }, [tools, activeNames, activeTypes, filterByName, filterByType]);

  // Columnas para la tabla de herramientas
  const toolColumns = useMemo(() => [
    { 
      headerName: 'Tipo', 
      field: 'tool.type', 
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      valueFormatter: params => getTipoDescripcion(params.value, TIPOS_EQUIPOS)
    },
    { 
      headerName: 'Nombre', 
      field: 'tool.name', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Serial', 
      field: 'serial', 
      width: 150,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Cantidad', 
      field: 'quantity', 
      width: 120,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      valueFormatter: params => params.value || 1
    }
  ], []);

  // Funciones para modal de asignar
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  const handleEquipmentsAssigned = () => {
    setShowAssignModal(false);
    
    if (showSnackbar) {
      showSnackbar('success', 'Herramientas asignadas correctamente');
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  // Funciones para modal de desasignar
  const handleOpenUnassignModal = () => {
    setShowUnassignModal(true);
  };

  const handleCloseUnassignModal = () => {
    setShowUnassignModal(false);
  };

  const handleEquipmentsUpdate = () => {
    setShowUnassignModal(false);
    
    if (showSnackbar) {
      showSnackbar('success', 'Herrmaientas actualizados correctamente');
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  // ✅ NUEVO: Funciones para modal de perfil de herramienta
  const handleCloseToolProfileModal = () => {
    setShowToolProfileModal(false);
    setSelectedToolId(null);
  };

  const handleToolProfileUpdate = () => {
    if (onDataChange) {
      onDataChange();
    }
    
    if (showSnackbar) {
      showSnackbar('success', 'Herramienta actualizada correctamente');
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Fila de filtros y botones */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {/* Filtros dropdown */}
            <div className="d-flex gap-2">
              <FilterSelect
                options={nameOptions}
                activeFilters={activeNames}
                onFiltersChange={handleNameChange}
                onOptionsUpdate={handleNameOptionsUpdate}
                title="Filtrar por nombre"
                filterKey={`work-${workId}-tools-names`}
                icon={<FaCubes />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={typeOptions}
                activeFilters={activeTypes}
                onFiltersChange={handleTypeChange}
                onOptionsUpdate={handleTypeOptionsUpdate}
                title="Filtrar por tipo"
                filterKey={`work-${workId}-tools-types`}
                icon={<FaTags />}
                variant="outline-secondary"
              />
            </div>
            
            {/* Botones de acción */}
            <div className="d-flex gap-2">
              <Button 
                variant="danger" 
                size="sm"
                className="d-flex align-items-center"
                onClick={handleOpenUnassignModal}
                disabled={tools.length === 0 || isWorkFinalized}
              >
                <FaMinus className="me-1" /> Desasignar Herramientas
              </Button>
              <Button 
                variant="primary"
                size="sm"
                className="d-flex align-items-center"
                onClick={handleOpenAssignModal}
                disabled={isWorkFinalized}
              >
                <FaPlus className="me-1" /> Asignar Herramientas
              </Button>
            </div>
          </div>
        </Col>
      </Row>
      
      {/* Tabla */}
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper
          columnDefs={toolColumns}
          rowData={filteredTools}
          tableType="equipos"
          onView={handleViewTool} // ✅ NUEVO: Habilitar visualización de perfil
          customGridOptions={{
            pagination: true,
            paginationPageSize: 20,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      {/* ✅ NUEVO: Modal específico para asignar equipos a obras */}
      <AssignEquipmentModal 
        show={showAssignModal}
        onHide={handleCloseAssignModal}
        workId={workId}
        onEquipmentsAssigned={handleEquipmentsAssigned}
      />
      
      {/* ✅ NUEVO: Modal específico para desasignar equipos de obras */}
      <UnassignEquipmentModal 
        show={showUnassignModal}
        onHide={handleCloseUnassignModal}
        workId={workId}
        assignedEquipments={tools}
        onEquipmentsUpdate={handleEquipmentsUpdate}
        showSnackbar={showSnackbar}
      />

      {/* ✅ NUEVO: Modal para ver perfil de herramienta - COMPONENTE REUTILIZABLE */}
      <ToolProfileModal
        show={showToolProfileModal}
        onHide={handleCloseToolProfileModal}
        toolId={selectedToolId}
        onToolUpdate={handleToolProfileUpdate}
        showSnackbar={showSnackbar} // Pasamos la función de snackbar del padre
        contextModule="works" // Indicamos que viene desde el módulo de obras
      />
    </div>
  );
};

export default ToolsSection;